<template>
	<div class="page-container" v-if="hasPermission('np.backoffice.users.show')">
		<div class="max-w-7xl mx-auto p-6">
			<!-- Header Section -->
			<div class="bg-white border-round-2xl shadow-1 p-6 mb-6">
				<div class="flex align-items-center justify-content-between">
					<div class="flex align-items-center gap-4">
						<div class="header-icon-container">
							<i class="fa fa-users text-white text-2xl"></i>
						</div>
						<div>
							<h1 class="font-bold text-gray-800 text-3xl m-0 mb-2">{{ pageTitle }}</h1>
							<p class="text-gray-600 m-0 text-lg">{{ pageDescription }}</p>
						</div>
					</div>
				</div>
			</div>

			<Loading v-if="loading" :full="false" :title="'Loading ' + pageTitle + '...'" />

			<!-- Main Content -->
			<div v-else class="bg-white border-round-2xl shadow-1 overflow-hidden">
				<!-- Table Header -->
				<div class="table-header-section">
					<div class="flex align-items-center gap-3 mb-4">
						<div class="bg-blue-100 p-2 border-round-lg">
							<i class="fa fa-list text-blue-600"></i>
						</div>
						<h2 class="text-xl font-semibold text-gray-800 m-0">Users Overview ({{ totalRecords }})</h2>
					</div>
					<div class="flex gap-3 flex-wrap">
						<Button type="button" icon="pi pi-filter-slash" label="Clear" class="modern-clear-button"
							@click="clearFilters()" />
						<span class="p-input-icon-left ml-auto modern-search-input">
							<i class="pi pi-id-card" />
							<InputText v-model="filterCard['global'].value" @keydown.enter="filterCallbackCard()" 
								v-tooltip.top="{ value: `Min. 4 characters`, escape: true }"
								placeholder="UserCard Search" />
						</span>
						<span class="p-input-icon-left modern-search-input">
							<i class="pi pi-search" />
							<InputText v-model="filters1['global'].value" @keydown.enter="filterCallback()" 
								v-tooltip.top="{ value: `Min. 4 characters`, escape: true }"
								placeholder="Keyword Search" />
						</span>
					</div>
				</div>

				<DataTable :value="entities" dataKey="id" lazy paginator 
					paginatorTemplate="PrevPageLink PageLinks NextPageLink RowsPerPageDropdown"
					:rows="pagingRequest.pageSize" :rowsPerPageOptions="[25, 50, 100]" 
					:totalRecords="totalRecords" 
					:first="pagingRequest.page * pagingRequest.pageSize"
					@page="onPage($event)" @row-click="onRowClicked"
					class="modern-datatable" :rowHover="true" responsiveLayout="scroll" 
					v-model:filters="filters1" filterDisplay="menu" 
					:globalFilterFields="['firstName', 'lastName', 'email']"
					stripedRows>
					<template #empty>
						<div class="text-center py-8">
							<i class="fa fa-inbox text-gray-400 text-4xl mb-3"></i>
							<p class="text-gray-500 text-lg m-0">No users found</p>
							<p class="text-gray-400 text-sm m-0 mt-2">Try adjusting your search criteria</p>
						</div>
					</template>
					<template #loading>
						<div class="text-center py-6">
							<i class="fa fa-spinner fa-spin text-blue-500 text-2xl mb-2"></i>
							<p class="text-gray-600 m-0">Loading users...</p>
						</div>
					</template>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-user text-gray-600"></i>
								<span class="font-semibold text-gray-700">Name</span>
							</div>
						</template>
						<template #body="{ data }">
							<div class="flex align-items-center gap-3 py-2">
								<img :src="getAccountCreationSourceImage(data.accountCreationSource)" 
									:alt="data.accountCreationSource || 'Unknown'" 
									class="account-source-icon" />
								<div>
									<p class="font-semibold text-gray-800 m-0">{{ data.firstName }} {{ data.lastName }}</p>
								</div>
							</div>
						</template>
					</Column>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-envelope text-gray-600"></i>
								<span class="font-semibold text-gray-700">Email</span>
							</div>
						</template>
						<template #body="{ data }">
							<span class="text-gray-700">{{ data.email }}</span>
						</template>
					</Column>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-phone text-gray-600"></i>
								<span class="font-semibold text-gray-700">Phone</span>
							</div>
						</template>
						<template #body="{ data }">
							<span class="text-gray-700">{{ data.phone }}</span>
						</template>
					</Column>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-calendar text-gray-600"></i>
								<span class="font-semibold text-gray-700">Joined</span>
							</div>
						</template>
						<template #body="{ data }">
							<div class="status-badge date-badge">
								<i class="fa fa-clock text-sm"></i>
								<span>{{ formatDate(data.created) }}</span>
							</div>
						</template>
					</Column>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-trophy text-gray-600"></i>
								<span class="font-semibold text-gray-700">Level</span>
							</div>
						</template>
						<template #body="{ data }">
							<div class="status-badge level-badge">
								<i class="fa fa-trophy text-sm"></i>
								<span class="font-semibold">{{ data.activeLevel || 1 }}</span>
							</div>
						</template>
					</Column>

					<Column header="">
						<template #header>
							<div class="flex align-items-center gap-2">
								<i class="fa fa-star text-gray-600"></i>
								<span class="font-semibold text-gray-700">Points</span>
							</div>
						</template>
						<template #body="{ data }">
							<div class="status-badge points-badge">
								<i class="fa fa-star text-sm"></i>
								<span>{{ data.currentPoints || 0 }}</span>
							</div>
						</template>
					</Column>
				</DataTable>
			</div>
		</div>
	</div>
</template>

<script>
import { ref, onMounted, inject } from 'vue';

import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';

import { FilterMatchMode, FilterOperator } from 'primevue/api';

import appleImage from '@/assets/login_options/apple.png';
import emailImage from '@/assets/login_options/email.png';
import googleImage from '@/assets/login_options/google.png';
import mpayImage from '@/assets/login_options/mpay.png';
import phoneImage from '@/assets/login_options/phone.png';

export default {
	inject: ['hasPermission'],
	components: {},
	setup() {
		const storeActionGetEntities = 'users/getUsersPaging';
		const storeGetterGetEntities = 'users/usersPagingObject';
		const entityName = 'User';
		const pageTitle = 'Users';
		const pageDescription = 'Here you can manage your Users';

		const loading = ref(true);
	
		const totalRecords = ref(0);
		const pagingRequest = ref({
			pageSize: 25,
			filter: null,
			page: 0,
			totalCount: null,
			tokens: {}
		})

		const pagingResponse = ref({
			hasMore: false,
			results: null,
			tokens: null,
			totalCount: null,
			totalPages: null
		})

		const filters1 = ref({
			global: { value: null, matchMode: FilterMatchMode.CONTAINS },
			firstName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
			lastName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
			email: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
		});

		const filterCard = ref({
			global: { value: null, matchMode: FilterMatchMode.CONTAINS },
		});

		const useFormatDate = inject('useFormatDate');

		const store = useStore();
		const router = useRouter();
		const toast = useToast();

		const entities = ref([]);

		const clearFilters = () => {
			
			const hasFilters = filters1.value.global?.value?.length > 0 || filterCard.value.global?.value?.length > 0
			
			initFilters();

			if(hasFilters){
				filterCallback();
				filterCallbackCard();
			}
			

		};
		const initFilters = () => {
			filters1.value = {
				global: { value: null, matchMode: FilterMatchMode.CONTAINS },
				firstName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
				lastName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
				email: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
			};
			filterCard.value = {
				global: { value: null, matchMode: FilterMatchMode.CONTAINS },
			}
		};

		const onPage = (event) => {
			var val = event;
			console.log("onPage", val)
			pagingRequest.value.page = val.page;
			if (pagingRequest.value.pageSize != val.rows) {
				pagingRequest.value.page = 1;
				pagingRequest.value.pageSize = val.rows;
				pagingRequest.value.totalCount = null;
				pagingRequest.value.tokens = null;
			}

			// lazyParams.value = event;
			loadRequiredData();
		};



		function onRowClicked(payload) {
			router.push({ name: 'edit-user', params: { userId: payload.data.id } });
		}

		async function filterCallback() {
			// console.log("onFilter - pagingRequest", filters1.value.global.value);
			// console.log("onFilter", filters1.value)
			if(filters1.value.global.value?.length <= 3 && filters1.value.global.value?.length != 0 ) return;
			pagingRequest.value.filter = filters1.value.global.value;
			pagingRequest.value.totalCount = null;
			pagingRequest.value.tokens = null;
		
			loadRequiredData();
		}

		async function filterCallbackCard(){
			
			if(filterCard.value.global.value?.length <= 3 && filterCard.value.global.value?.length != 0 ) return;
			pagingRequest.value.cardFilter = filterCard.value.global.value;
			pagingRequest.value.totalCount = null;
			pagingRequest.value.tokens = null;

			loadRequiredData();

		}

		async function reloadEntities() {
			try {
				// console.log("reloadEntities - pagingRequest", pagingRequest.value);
				await store.dispatch(storeActionGetEntities, pagingRequest.value);
				pagingResponse.value = store.getters[storeGetterGetEntities];

				// console.log("reloadEntities - pagingResponse", pagingResponse.value);
				totalRecords.value = pagingResponse.value.totalCount;
				pagingRequest.value.totalCount = pagingResponse.value.totalCount;
				pagingRequest.value.tokens = pagingResponse.value.tokens;


				// const loadedEntities = pagingObject.results
				entities.value = pagingResponse.value.results;
			} catch (err) {
				toast.add({ severity: 'error', summary: 'Error', detail: 'Could not load ' + pageTitle + ': ' + err, life: 3000 });
			}
		}

		async function loadRequiredData() {
			loading.value = true;

			await reloadEntities();

			loading.value = false;
		}

		onMounted(async () => {
			await loadRequiredData();
		});

		function getProperty(source, field) {
			return this[source][field];
		}

		function updateProperty(source, field, payload) {
			this[source][field] = payload;
		}

		function getAccountCreationSourceImage(accountCreationSource) {
			if (!accountCreationSource) {
				return phoneImage;
			}
			
			const source = accountCreationSource.toLowerCase();
			switch (source) {
				case 'mobilepay':
					return mpayImage;
				case 'apple':
					return appleImage;
				case 'google':
					return googleImage;
				case 'email':
					return emailImage;
				default:
					return phoneImage;
			}
		}

		return {
			getProperty,
			updateProperty,
			clearFilters,
			onPage,

			entityName,
			pageTitle,
			pageDescription,
			loading,
			filters1,
			filterCard,
			pagingRequest,

			entities,
			totalRecords,

			onRowClicked,
			filterCallback,
			filterCallbackCard,

			formatDate: useFormatDate().formatDateTimeFull,
			getAccountCreationSourceImage,
		};
	},
};
</script>

<style scoped>
/* Fixed background that doesn't scroll */
.page-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%);
  background-attachment: fixed;
}

/* Header icon container */
.header-icon-container {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
  padding: 0.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Table header section */
.table-header-section {
  background: linear-gradient(90deg, #f9fafb 0%, #f1f5f9 100%);
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

/* Modern search inputs */
.modern-search-input :deep(input) {
  border-radius: 0.75rem;
  border: 2px solid #e5e7eb;
  padding: 0.625rem 1rem 0.625rem 2.5rem;
  transition: all 0.2s ease;
}

.modern-search-input :deep(input:focus) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Modern clear button */
.modern-clear-button {
  background: white !important;
  border: 2px solid #e5e7eb !important;
  color: #6b7280 !important;
  border-radius: 0.75rem !important;
  padding: 0.625rem 1rem !important;
  font-weight: 500 !important;
  transition: all 0.2s ease !important;
}

.modern-clear-button:hover {
  background: #f9fafb !important;
  border-color: #9ca3af !important;
  color: #374151 !important;
}

/* Status badge base styling */
.status-badge {
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  font-weight: 500;
  text-align: center;
  min-width: max-content;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  color: white;
  transition: all 0.2s ease;
}

.status-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Specific badge colors */
.date-badge {
  background: linear-gradient(135deg, #fbbf24 0%, #f97316 100%);
}

.level-badge {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.points-badge {
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
}

/* Custom styling for modern datatable */
:deep(.modern-datatable) {
  border: none;
  border-radius: 0;
}

:deep(.modern-datatable .p-datatable-header) {
  background: transparent;
  border: none;
  padding: 0;
}

:deep(.modern-datatable .p-datatable-thead > tr > th) {
  background: transparent;
  border: none;
  padding: 1rem 1.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

:deep(.modern-datatable .p-datatable-tbody > tr) {
  background: white;
  border: none;
  transition: all 0.2s ease;
  cursor: pointer;
}

:deep(.modern-datatable .p-datatable-tbody > tr:hover) {
  background: #f8fafc;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

:deep(.modern-datatable .p-datatable-tbody > tr > td) {
  border: none;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
}

:deep(.modern-datatable .p-datatable-tbody > tr:last-child > td) {
  border-bottom: none;
}

/* Striped rows with subtle styling */
:deep(.modern-datatable .p-datatable-tbody > tr:nth-child(even)) {
  background: #fdfdfe;
}

:deep(.modern-datatable .p-datatable-tbody > tr:nth-child(even):hover) {
  background: #f1f5f9;
}

/* Paginator styling */
:deep(.modern-datatable .p-paginator) {
  background: transparent;
  border: none;
  padding: 1.5rem;
  border-top: 1px solid #f1f5f9;
}

:deep(.modern-datatable .p-paginator .p-paginator-pages .p-paginator-page) {
  border-radius: 0.5rem;
  min-width: 2.5rem;
  height: 2.5rem;
  transition: all 0.2s ease;
}

:deep(.modern-datatable .p-paginator .p-paginator-pages .p-paginator-page.p-highlight) {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
  border: none;
  color: white !important;
}

/* Loading and empty state styling */
.text-center i {
  display: block;
  margin: 0 auto;
}

/* Responsive improvements */
@media (max-width: 768px) {
  :deep(.modern-datatable .p-datatable-thead > tr > th),
  :deep(.modern-datatable .p-datatable-tbody > tr > td) {
    padding: 0.75rem 1rem;
  }
  
  .max-w-7xl {
    max-width: 100%;
  }
  
  .p-6 {
    padding: 1rem;
  }
  
  .status-badge {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }
}

/* Smooth scrolling for responsive table */
:deep(.p-datatable-wrapper) {
  border-radius: 0;
}

/* Ensure proper shadow styling */
.shadow-1 {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

/* Account creation source icon styling */
.account-source-icon {
  width: 1.5rem;
  height: 1.5rem;
  object-fit: contain;
}
</style>
